import {
  CsvExportParams,
  GridApi,
  ProcessCellForExportParams
} from "ag-grid-enterprise";
import { ArrayStringCellRenderer } from "entity-app/shared-components/log-render-components/cell-renders";
import { formatLinkedDesignPackagesForExport } from "pages/preconstruction-log-page/hooks/linked-design-packages";
import {
  designScopeCsvDesignPackageContinuationRows,
  designScopeCsvFirstDesignPackage,
  designScopeLinkedPackagesForExport
} from "pages/design-scope-page/shared/design-scope-package-column-values";

const asArray = <T>(value: unknown): T[] => {
  if (value === undefined || value === null) return [];
  return Array.isArray(value) ? (value as T[]) : ([value] as T[]);
};

const ARRAY_STRING_COLUMN_IDS = new Set([
  "linked_submittals",
  "linked_materials",
  "linked_tasks",
  "locations"
]);

const isArrayStringColumn = (colDef: {
  colId?: string;
  cellRenderer?: unknown;
}) =>
  (colDef.colId && ARRAY_STRING_COLUMN_IDS.has(colDef.colId)) ||
  colDef.cellRenderer === ArrayStringCellRenderer;

const formatArrayStringValue = (value: unknown) => {
  const arr = (value || []) as Array<{
    id: string;
    name: string;
    title: string;
    deleted_from_source?: boolean;
  }>;

  return arr
    .filter((item) => !item.deleted_from_source)
    .map((item) => item.name || item.title)
    .join("\n");
};

const formatReviewResponseValue = (value: unknown) => {
  if (typeof value === "string") {
    return value;
  }

  const items = asArray<{
    response: string | null;
    user_id: string | null;
    user_source_info_id: string | null;
    username: string | null;
  }>(value);

  return items
    .map((item) => {
      const response = item?.response || null;
      const userName = item?.username || "";
      return userName ? `${response || ""} (${userName})` : response || "";
    })
    .join("\n");
};

const getRowGroupValues = (node: ProcessCellForExportParams<any>["node"]) => {
  const rowGroupValues: string[] = [];
  let currentParent = node?.parent;

  while (currentParent && currentParent.level >= 0) {
    if (currentParent.key !== undefined && currentParent.key !== null) {
      const groupValue = String(currentParent.key).trim();
      if (groupValue) {
        rowGroupValues.unshift(groupValue);
      }
    }
    currentParent = currentParent.parent;
  }

  return rowGroupValues;
};

const getSkippedRowGroupFormattedValue = (
  params: ProcessCellForExportParams<any>
): string | undefined => {
  const { value, column, node } = params;
  const colDef = column.getColDef();
  const isAutoGroupColumn =
    !!colDef.showRowGroup || column.getColId().includes("ag-Grid-AutoColumn");
  const rowGroupValues = getRowGroupValues(node);

  if (
    isAutoGroupColumn &&
    !node?.group &&
    !value &&
    rowGroupValues.length > 0
  ) {
    return rowGroupValues.join(" / ");
  }

  return undefined;
};

const getFormattedValues = (
  params: ProcessCellForExportParams<any>,
  options?: { skipRowGroups?: boolean; appendDesignPackageRows?: boolean }
) => {
  const { value, column, formatValue, node, context } = params;
  const colDef = column.getColDef();
  // Sole caller (line ~133) always passes an options object, so the `|| {}`
  // fallback for a missing/undefined `options` argument is never reached.
  /* v8 ignore next */
  const { skipRowGroups = false } = options || {};

  if (skipRowGroups) {
    const skippedRowGroupValue = getSkippedRowGroupFormattedValue(params);
    if (skippedRowGroupValue !== undefined) {
      return skippedRowGroupValue;
    }
  }

  const formattedValue = formatValue(value);

  if (colDef.colId === "linked_design_packages") {
    return formatLinkedDesignPackagesForExport(value);
  }

  if (isArrayStringColumn(colDef)) {
    return formatArrayStringValue(value);
  }

  if (colDef.colId === "not_found_in_source") {
    return value ? "Missing" : "";
  }

  if (colDef.colId === "review_response" || colDef.colId === "final_response") {
    return formatReviewResponseValue(value);
  }

  if (options?.appendDesignPackageRows && colDef.colId === "designPackage") {
    const firstPackage = designScopeCsvFirstDesignPackage({
      data: node?.data,
      context
    });
    if (firstPackage !== undefined) return firstPackage;
  }

  return designScopeLinkedPackagesForExport({
    colId: colDef.colId,
    value: (formattedValue || value) ?? "",
    data: node?.data,
    context
  }) as string;
};

type TSortPosition = {
  colId: string;
  dependentColId: string;
  position: "before" | "after";
};

const getParams = (props: {
  filename: string;
  api: GridApi;
  includedColumns: Array<string>;
  excludedColumns: Array<string>;
  sortPosition?: Array<TSortPosition>;
  skipRowGroups?: boolean;
  appendDesignPackageRows?: boolean;
}): CsvExportParams => {
  const {
    api,
    filename,
    includedColumns,
    excludedColumns,
    sortPosition = [],
    skipRowGroups = false,
    appendDesignPackageRows = false
  } = props;
  const columnKeys = api
    ?.getAllGridColumns()
    ?.filter((col) => {
      const colId = col.getColId();
      if (includedColumns.includes(colId)) return true;

      if (excludedColumns.includes(colId)) return false;

      return col.isVisible();
    })
    .map((col) => col.getColId());

  sortPosition.forEach((sort) => {
    const dependentColumnIndex = columnKeys.indexOf(sort.dependentColId);
    // If dependent column is found, we just insert after the current column
    if (dependentColumnIndex > -1) {
      columnKeys.splice(dependentColumnIndex + 1, 0, sort.colId);
    }
  });

  return {
    columnKeys,
    processCellCallback(params): string {
      return getFormattedValues(params, {
        skipRowGroups,
        appendDesignPackageRows
      });
    },
    ...(appendDesignPackageRows
      ? {
          getCustomContentBelowRow(params) {
            const rows = designScopeCsvDesignPackageContinuationRows({
              data: params.node?.data,
              context: params.context,
              columnIds: columnKeys
            });
            if (!rows.length) return undefined;
            return rows.map((row) => row.map((value) => ({ data: { value } })));
          }
        }
      : {}),
    skipRowGroups,
    fileName: filename
  };
};

export const exportEntityLogAsCSV = (props: {
  filename: string;
  gridRef: {
    api: GridApi<any>;
  };
  includedColumns: Array<string>;
  excludedColumns: Array<string>;
  sortPosition?: Array<TSortPosition>;
  skipRowGroups?: boolean;
  /** Design Scope CSV: one following row per extra design package. */
  appendDesignPackageRows?: boolean;
}) => {
  const {
    gridRef,
    filename,
    includedColumns,
    excludedColumns,
    sortPosition,
    skipRowGroups,
    appendDesignPackageRows
  } = props;
  const { api } = gridRef;

  api.exportDataAsCsv(
    getParams({
      filename,
      api,
      includedColumns,
      excludedColumns,
      sortPosition,
      skipRowGroups,
      appendDesignPackageRows
    })
  );
};
